import React, { useState, useRef, useEffect } from 'react';
import { Switch, Button, Col, Drawer, Form, Input, Row, Select, Space, message, InputNumber, DatePicker } from 'antd';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { insert, update, getById } from '../../../../apis/OdooTimeLeaveApi';
import { getEmpleadosActivos } from '../../../../apis/OdooApi';
import { getList as getLeaveTypes } from '../../../../apis/OdooLeaveTypeApi';
import { getList as getDepartamentos } from '../../../../apis/OdooDepartamentoApi';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

const App = ({ title, id, open, finish }) => {
    const [form] = Form.useForm();
    const [ID, setID] = useState(id);
    const [modified, setModified] = useState(false);
    const [initialValues, setInitialValues] = useState(null);
    const [empleados, setEmpleados] = useState([]);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [departamentos, setDepartamentos] = useState([]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if (!values) return;
        } catch (errorInfo) {
            console.log('Failed:', errorInfo);
            return;
        }

        let success = false;
        const fields = form.getFieldsValue();

        if (fields.date_from) {
            fields.date_from = fields.date_from.format('YYYY-MM-DD');
        }
        if (fields.date_to) {
            fields.date_to = fields.date_to.format('YYYY-MM-DD');
        }

        if (ID > 0) {
            const payload = {
                ...fields
            };
            const response = await update(ID, payload);
            success = response.ok;
            if (response.ok) {
                message.success('Se actualizó correctamente el tiempo de ausencia!');
            }
        } else {
            const payload = {
                ...fields
            };
            const response = await insert(payload);
            success = response.ok;
            if (response.ok) {
                setID(response.payload);
                message.success('Se guardó correctamente el tiempo de ausencia!');
            }
        }

        if (success) {
            setModified(true);
            onClose(true);
        }
    };

    const onClose = (reload) => {
        finish(reload ?? modified);
        setInitialValues(null);
    };

    const effectRan = useRef(false);
    useEffect(() => {
        if (!effectRan.current) {
            (async () => {
                await getEmpleadosOdoo();
                await getLeaveTypesOdoo();
                await getDepartamentosOdoo();

                if (ID > 0) {
                    await refreshData();
                } else {
                    // Set default values for new records
                    setInitialValues({
                        activo: true,
                        number_of_days: 0,
                        number_of_hours: 0,
                        leave_type_request_unit: 'day',
                        description: '',
                        date_from: dayjs(),
                        date_to: dayjs()
                    });
                }
                setModified(false);
            })();
        }
        return () => effectRan.current = true;
    }, []);

    const refreshData = async () => {
        const response = await getById(ID);
        if (response.ok) {
            let item = response.payload;
            if (item.date_from) {
                item.date_from = dayjs(item.date_from);
            }
            if (item.date_to) {
                item.date_to = dayjs(item.date_to);
            }
            if (item.empleado_id) {
                item.employee_id = item.empleado_id;
            }
            if (item.holiday_status_id) {
                item.holiday_status_id = item.holiday_status_id;
            }
            if (item.department_id) {
                item.department_id = item.department_id;
            }
            setInitialValues(item);
        }
    };

    const getEmpleadosOdoo = async () => {
        const response = await getEmpleadosActivos();
        if (response.ok) {
            let items = response.payload;
            setEmpleados(items);
        }
    };

    const getLeaveTypesOdoo = async () => {
        const response = await getLeaveTypes();
        if (response.ok) {
            let items = response.payload;
            setLeaveTypes(items);
        }
    };

    const getDepartamentosOdoo = async () => {
        const response = await getDepartamentos();
        if (response.ok) {
            let items = response.payload;
            setDepartamentos(items);
        }
    };

    const handleLeaveTypeChange = (value) => {
        const selectedLeaveType = leaveTypes.find(lt => lt.id === value);
        if (selectedLeaveType) {
            form.setFieldsValue({
                holiday_status_name: selectedLeaveType.name
            });
        }
    };

    const handleDepartmentChange = (value) => {
        const selectedDepartment = departamentos.find(dept => dept.odoo_id === value);
        form.setFieldsValue({
            department_name: selectedDepartment ? selectedDepartment.name : ''
        });
    };

    return (
        <Drawer
            title={title}
            width={720}
            placement={"right"}
            onClose={onClose}
            open={open}
            closable={true}
            extra={<Space>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleOk} type="primary">
                    Guardar
                </Button>
            </Space>}
        >
            {
                initialValues &&
                <Form
                    layout="vertical"
                    form={form}
                    requiredMark
                    autoComplete="off"
                    initialValues={initialValues}
                    onFinish={handleOk}>
                    <Row gutter={16}>
                        <Col md={{ span: 24 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="employee_id"
                                label="Empleado"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Selecciona un empleado',
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Por favor selecciona un empleado"
                                    disabled={ID > 0}
                                >
                                    {
                                        empleados.map((item) => <Option key={item.value} value={item.value}>{item.text}</Option>)
                                    }
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 24 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="description"
                                label="Descripción"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor ingresa una descripción',
                                    },
                                ]}
                            >
                                <TextArea
                                    placeholder="Descripción del tiempo de ausencia..."
                                    rows={3}
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="holiday_status_id"
                                label="Tipo de Ausencia"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor selecciona el tipo de ausencia',
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Selecciona el tipo de ausencia"
                                    disabled={ID > 0}
                                    onChange={handleLeaveTypeChange}
                                >
                                    {
                                        leaveTypes.map((item) => (
                                            <Option key={item.id} value={item.id}>
                                                {item.display_name || item.name}
                                            </Option>
                                        ))
                                    }
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="department_id"
                                label="Departamento"
                            >
                                <Select
                                    placeholder="Selecciona el departamento"
                                    allowClear
                                    onChange={handleDepartmentChange}
                                >
                                    {
                                        departamentos.map((item) => (
                                            <Option key={item.odoo_id} value={item.odoo_id}>
                                                {item.text || item.text}
                                            </Option>
                                        ))
                                    }
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="holiday_status_name"
                                label="Nombre del Tipo"
                                hidden
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="department_name"
                                label="Nombre del Departamento"
                                hidden
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="date_from"
                                label="Fecha Desde"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor selecciona la fecha de inicio',
                                    },
                                ]}
                            >
                                <DatePicker
                                    placeholder="Selecciona la fecha de inicio"
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="date_to"
                                label="Fecha Hasta"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor selecciona la fecha final',
                                    },
                                ]}
                            >
                                <DatePicker
                                    placeholder="Selecciona la fecha final"
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="leave_type_request_unit"
                                label="Unidad de Solicitud"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor selecciona la unidad',
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Selecciona la unidad"
                                >
                                    <Option value="day">Días</Option>
                                    <Option value="hour">Horas</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="number_of_days"
                                label="Número de Días"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor ingresa el número de días',
                                    },
                                ]}
                            >
                                <InputNumber
                                    placeholder="Número de días"
                                    style={{ width: '100%' }}
                                    min={0}
                                    step={0.5}
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="number_of_hours"
                                label="Número de Horas"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor ingresa el número de horas',
                                    },
                                ]}
                            >
                                <InputNumber
                                    placeholder="Número de horas"
                                    style={{ width: '100%' }}
                                    min={0}
                                    step={0.5}                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="activo"
                                label="Activo"
                                valuePropName="checked"
                            >
                                <Switch
                                    checkedChildren={<CheckOutlined />}
                                    unCheckedChildren={<CloseOutlined />}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            }
        </Drawer>
    );
};

export default App;